"use client";

import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import parse from "html-react-parser";

interface DescriptionExtendProps {
  cmsContentData: string;
}
const CMSPageParseDescriptionExtend: React.FC<DescriptionExtendProps> = ({
  cmsContentData,
}) => {
  const [sanitizedContent, setSanitizedContent] = useState("");

  useEffect(() => {
    // Configure DOMPurify to allow figure and oembed tags
    DOMPurify.addHook("uponSanitizeElement", (node, data) => {
      if (data.tagName === "figure" || data.tagName === "oembed") {
        return node;
      }
    });

    const sanitized = DOMPurify.sanitize(cmsContentData, {
      ADD_TAGS: ["figure", "oembed"],
      ADD_ATTR: ["url"],
    });

    const processedContent = sanitized.replace(
      /<oembed url="https:\/\/www\.youtube\.com\/watch\?v=([\w-]+)(&[^\"]*)?"><\/oembed>/g,
      (_, videoId) => `
        <div className="cms-video-container">
          <iframe
            src="https://www.youtube.com/embed/${videoId}" 
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowfullscreen
          ></iframe>
        </div>
      `,
    );

    setSanitizedContent(processedContent);

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = sanitized;

    const headings = Array.from(tempDiv.querySelectorAll("h2"));
    headings.map((h2, index) => ({
      id: `section-${index}`,
      title: h2.textContent || "",
    }));
  }, [cmsContentData]);

  useEffect(() => {
    // Add ids to h2 elements in the actual content
    const contentDiv = document.getElementById("cms-content");

    // Remove all <strong> tags from the content
    if (contentDiv) {
      const strongTags = contentDiv.getElementsByTagName("strong");
      while (strongTags.length > 0) {
        const strongTag = strongTags[0];
        const textNode = document.createTextNode(strongTag.textContent || "");
        strongTag.parentNode?.replaceChild(textNode, strongTag);
      }
    }

    if (contentDiv) {
      const heading1 = Array.from(contentDiv.querySelectorAll("h1"));
      heading1.forEach((h1, index) => {
        h1.id = `section-${index}`;
        h1.classList.add("text-[32px]");
        h1.classList.add("font-bold");
        h1.classList.add("font-work-sans");
        h1.classList.add("py-2");
        h1.classList.add("leading-8");
      });
      const heading2 = Array.from(contentDiv.querySelectorAll("h2"));
      heading2.forEach((h2, index) => {
        h2.id = `section-${index}`;
        h2.classList.add("text-2xl");
        h2.classList.add("font-semibold");
        h2.classList.add("font-work-sans");
        h2.classList.add("py-2");
        h2.classList.add("leading-8");
      });

      const heading3 = Array.from(contentDiv.querySelectorAll("h3"));
      heading3.forEach((h3, index) => {
        h3.id = `section-${index}`;
        h3.classList.add("text-xl");
        h3.classList.add("font-semibold");
        h3.classList.add("font-work-sans");
        h3.classList.add("py-2");
        h3.classList.add("leading-8");
      });

      const heading4 = Array.from(contentDiv.querySelectorAll("h4"));
      heading4.forEach((h4, index) => {
        h4.id = `section-${index}`;
        h4.classList.add("text-base");
        h4.classList.add("font-semibold");
        h4.classList.add("font-work-sans");
        h4.classList.add("py-2");
        h4.classList.add("leading-8");
      });

      const heading5 = Array.from(contentDiv.querySelectorAll("h5"));
      heading5.forEach((h5, index) => {
        h5.id = `section-${index}`;
        h5.classList.add("text-sm");
        h5.classList.add("font-semibold");
        h5.classList.add("font-work-sans");
        h5.classList.add("py-2");
        h5.classList.add("leading-8");
      });

      const heading6 = Array.from(contentDiv.querySelectorAll("h6"));
      heading6.forEach((h6, index) => {
        h6.id = `section-${index}`;
        h6.classList.add("text-xs");
        h6.classList.add("font-semibold");
        h6.classList.add("font-work-sans");
        h6.classList.add("py-2");
        h6.classList.add("leading-8");
      });

      const paragraphs = Array.from(contentDiv.querySelectorAll("p"));
      paragraphs.forEach((p) => {
        p.classList.add("font-work-sans");
        p.classList.add("text-sm");
        p.classList.add("md:text-base");
        p.classList.add("py-2");
      });

      // add styles for tables, tbody, thead, tr, td, th and its related tags
      const tables = Array.from(contentDiv.querySelectorAll("table"));
      tables.forEach((table) => {
        table.removeAttribute("style");
        table.classList.add("table-auto");
        table.classList.add("w-full");
        table.classList.add("border-collapse");
        table.classList.add("border-2");
        table.classList.add("border-gray-300");
      });

      const tbody = Array.from(contentDiv.querySelectorAll("tbody"));
      tbody.forEach((tbody) => {
        tbody.classList.add("bg-gray-100");
      });

      const thead = Array.from(contentDiv.querySelectorAll("thead"));
      thead.forEach((thead) => {
        thead.classList.add("bg-gray-100");
      });

      const tr = Array.from(contentDiv.querySelectorAll("tr"));
      tr.forEach((tr) => {
        tr.classList.add("bg-gray-100");
      });

      const td = Array.from(contentDiv.querySelectorAll("td"));
      td.forEach((td) => {
        td.classList.add("border-2");
        td.classList.add("border-gray-300");
      });

      const th = Array.from(contentDiv.querySelectorAll("th"));
      th.forEach((th) => {
        th.classList.add("border-2");
        th.classList.add("border-gray-300");
      });

      const images = Array.from(contentDiv.querySelectorAll("img"));
      images.forEach((img) => {
        img.classList.add("mt-[12px]");
        img.classList.add("h-full");
        img.classList.add("w-full");
        img.classList.add("md:w-auto");
        img.classList.add("md:h-auto");
        img.classList.add("rounded-lg");
        img.classList.add("object-contain");
        img.classList.add("md:object-cover");
        // Remove inline styles from images
        img.removeAttribute("style");
      });

      // Add styles for unordered and ordered lists
      const listItems = Array.from(contentDiv.querySelectorAll("li"));
      listItems.forEach((li) => {
        li.classList.add("font-work-sans", "text-sm", "md:text-base", "py-2");
      });

      // Add styles for unordered lists
      const unorderedLists = Array.from(contentDiv.querySelectorAll("ul"));
      unorderedLists.forEach((ul) => {
        ul.classList.add("list-disc", "pl-5", "mb-4");
      });

      // Add styles for ordered lists
      const orderedLists = Array.from(contentDiv.querySelectorAll("ol"));
      orderedLists.forEach((ol) => {
        ol.classList.add("list-decimal", "pl-5", "mb-4");
      });

      // Add styles for underlined text
      const underlinedText = Array.from(contentDiv.querySelectorAll("u"));
      underlinedText.forEach((u) => {
        u.classList.add("underline");
      });

      const a = Array.from(contentDiv.querySelectorAll("a"));
      a.forEach((item) => {
        item.classList.add("text-blue-500");
      });
    }
  }, [sanitizedContent]);
  return (
    <div className="relative flex flex-col md:flex-row">
      <div id="cms-content">{parse(sanitizedContent)}</div>
    </div>
  );
};
export default CMSPageParseDescriptionExtend;
